import { platform } from "./platform/index.js";

/**
 * إعداد وقت التشغيل — **يُقرأ من ملف، لا يُخبَز في الحزمة**.
 *
 * نفس الملفات المبنيّة تعمل عند المشتري وفي الديمو وعند TWING؛ ما يتغيّر هو
 * `config.json` وحده. في v2 كان عنوان السيرفر يُخبَز وقت البناء، فكان كل تغيير
 * بيئة يعني إعادة بناء — **وتسع حوادث** سببها حزمةٌ بُنيت بإعداد بيئةٍ أخرى.
 */

export type RuntimeConfig = {
  readonly apiBaseUrl: string;
  /** توقيت شاشات الموظفين — من سجل الإعدادات، لا من جهاز القارئ */
  readonly systemTimezone: string;
  /** اسم النظام ولونه: يظهران قبل تسجيل الدخول، فلا ينتظران `/me` */
  readonly systemName: string;
  readonly accentColor: string;
  /** مفتاحُ تنبيه الأجهزة العامّ — **فراغُه يعني أن التنبيه غيرُ مضبوط** */
  readonly pushPublicKey: string;
  /**
   * **وهل البريدُ مُهيَّأ؟** — به تُعرَض «نسيت كلمة السر؟» أو لا تُعرَض.
   *
   * ورابطٌ بلا قناةٍ خلفه هو منعُ v2 الحرفيّ: شاشةٌ تقول «بعتنا لك رابط»
   * ولا شيء وراءها. **وافتراضُه `false`**: سيرفرٌ قديم لا يَعِد بما لا يملك.
   */
  readonly mailEnabled: boolean;
  /** **علامةُ مِنوال في القوقعة** — لتنصيب TWING وحده، والمشتري بحرفه */
  readonly minwalMark: boolean;
};

const FALLBACK: RuntimeConfig = {
  apiBaseUrl: "/api/v1",
  systemName: "مِنوال",
  accentColor: "#0B6E75",
  systemTimezone: "Africa/Cairo",
  pushPublicKey: "",
  mailEnabled: false,
  minwalMark: false,
};

/**
 * يقرأ الإعداد، **ولا يسقط إن غاب**.
 *
 * ملفٌ ناقص يعني نظاماً بهوية افتراضية — لا شاشةً بيضاء. وأسوأ ما يمكن أن
 * يحدث لمشترٍ يشغّل النظام لأول مرة هو صفحةٌ فارغة بلا سبب.
 */
/**
 * يقرأ الهوية من السيرفر — **فما يغيّره المشتري في شاشته يُرى**.
 *
 * والملفّ يبقى احتياطاً وحده: لو لم يستجب السيرفر، تُعرَض شاشة الدخول بهوية
 * الملفّ بدل شاشةٍ بيضاء. وفي v2 كانت الهوية في ملفّ نشرٍ وحده، فإعدادُ اسم
 * النظام ولونه يُحفَظ ولا يفعل شيئاً.
 */
async function loadIdentity(base: RuntimeConfig): Promise<RuntimeConfig> {
  try {
    const response = await platform.fetch(`${base.apiBaseUrl}/identity`, { cache: "no-store" });
    if (!response.ok) return base;
    const body = (await response.json()) as { data?: Partial<RuntimeConfig> };
    const data = body.data ?? {};
    return {
      ...base,
      systemName: data.systemName ?? base.systemName,
      accentColor: data.accentColor ?? base.accentColor,
      systemTimezone: data.systemTimezone ?? base.systemTimezone,
      pushPublicKey: data.pushPublicKey ?? base.pushPublicKey,
      mailEnabled: data.mailEnabled === true,
      minwalMark: data.minwalMark === true,
    };
  } catch {
    return base;
  }
}

export async function loadConfig(): Promise<RuntimeConfig> {
  try {
    const response = await platform.fetch("/config.json", { cache: "no-store" });
    if (!response.ok) return FALLBACK;
    const parsed = (await response.json()) as Partial<RuntimeConfig>;
    return loadIdentity({
      apiBaseUrl: parsed.apiBaseUrl ?? FALLBACK.apiBaseUrl,
      systemName: parsed.systemName ?? FALLBACK.systemName,
      accentColor: parsed.accentColor ?? FALLBACK.accentColor,
      systemTimezone: parsed.systemTimezone ?? FALLBACK.systemTimezone,
      pushPublicKey: FALLBACK.pushPublicKey,
      mailEnabled: FALLBACK.mailEnabled,
      minwalMark: FALLBACK.minwalMark,
    });
  } catch {
    return loadIdentity(FALLBACK);
  }
}
